import { trpc } from "@/lib/trpc";
import { useQuery } from "@tanstack/react-query";
import { getDevPlanOverride } from "@/utils/dev-tools";

export interface FeatureFlagResult {
  enabled: boolean;
  loading: boolean;
  reason?: string;
  metadata?: {
    trial?: boolean;
    trialEnd?: Date | null;
    [key: string]: any;
  };
}

/**
 * Hook to check if a feature flag is enabled
 *
 * @param enabled - Pass false to skip the query entirely (e.g. read-only/public
 * views that have no authenticated session and would otherwise 401).
 */
export function useFeatureFlag(key: string, enabled: boolean = true): FeatureFlagResult {
  const queryOptions = trpc.featureFlags.check.queryOptions({ key });

  const { data, isLoading } = useQuery({
    ...queryOptions,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    enabled: data?.enabled ?? false,
    loading: isLoading,
    reason: data?.reason,
    metadata: data?.metadata,
  };
}

/**
 * Hook to check multiple feature flags at once
 */
export function useFeatureFlags(keys: string[]): {
  flags: Record<string, boolean>;
  loading: boolean;
} {
  const queryOptions = trpc.featureFlags.checkBatch.queryOptions({ keys });
  
  const { data, isLoading } = useQuery({
    ...queryOptions,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  
  return {
    flags: data ?? {},
    loading: isLoading,
  };
}

/**
 * Hook to get all feature flags for the current user
 *
 * In development mode, you can override the plan using browser console:
 * ```
 * window.devTools.setPlanOverride('FREE')
 * ```
 */
export function useAllFeatureFlags() {
  const queryOptions = trpc.featureFlags.myFlags.queryOptions();

  const { data, isLoading, refetch } = useQuery({
    ...queryOptions,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Apply dev plan override in development mode
  const devPlanOverride = getDevPlanOverride();
  const effectivePlan = devPlanOverride ?? data?.plan ?? "FREE";

  return {
    flags: data?.flags ?? {},
    plan: effectivePlan,
    trial: data?.trial ?? false,
    trialEnd: data?.trialEnd,
    loading: isLoading,
    refetch,
  };
}